import type * as React from "react";
import { Badge } from "../../components/ui/badge";
import { Box } from "../../components/ui/box";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Text } from "../../components/ui/text";
import {
  useEntitySelector,
  type EntityItemConfig,
  type EntitySelection,
} from "./entity-selector-context";
import { EntitySelectorItem } from "./entity-selector-item";
import type { Omittable } from "../../lib/props";

export type EntitySelectorSelectedProps = Omittable<{
  label?: string;
  hideWhenEmpty?: boolean;
  hideBadge?: boolean;
  hideSelectionHighlight?: boolean;
  /** Max height in pixels for the selected items area. When set, items become scrollable. */
  maxHeight?: number;
  /** Return an action label per selected item. When provided, the action button appears on hover. */
  getItemActionLabel?: (selection: EntitySelection) => string | undefined;
  /** Called when an item's action button is clicked. */
  onItemAction?: (entity: EntityItemConfig) => void;
  /** Return a secondary action label per selected item. */
  getItemSecondaryActionLabel?: (
    selection: EntitySelection,
  ) => string | undefined;
  /** Called when an item's secondary action button is clicked. */
  onItemSecondaryAction?: (entity: EntityItemConfig) => void;
  children?: React.ReactNode;
  ref?: React.Ref<HTMLDivElement>;
}>;

function EntitySelectorSelected({
  label = "Selected",
  hideWhenEmpty = false,
  hideBadge = false,
  hideSelectionHighlight = false,
  maxHeight,
  getItemActionLabel,
  onItemAction,
  getItemSecondaryActionLabel,
  onItemSecondaryAction,
  children,
  ref,
}: EntitySelectorSelectedProps) {
  const { selections, getPoolBadge, variant } = useEntitySelector();

  // Drawer presentation is the touch surface: hover is unavailable, so item
  // actions render inline, and the DrawerPanel owns the ONE scrollable — this
  // section must not mount a nested scroller there.
  const isDrawer = variant === "drawer";

  if (hideWhenEmpty && selections.length === 0) {
    return null;
  }

  if (children) {
    return (
      <Box ref={ref} border="bottom">
        {children}
      </Box>
    );
  }

  const items =
    selections.length > 0 ? (
      selections.map((selection) => {
        const badge = getPoolBadge(selection.poolId);
        const itemActionLabel = getItemActionLabel?.(selection) ?? badge?.label;
        const itemSecondaryActionLabel =
          getItemSecondaryActionLabel?.(selection);
        return (
          <EntitySelectorItem
            key={selection.id}
            entity={selection.entity}
            poolId={selection.poolId}
            actionLabel={itemActionLabel}
            onAction={onItemAction}
            secondaryActionLabel={itemSecondaryActionLabel}
            onSecondaryAction={onItemSecondaryAction}
            showCheckbox
            hideSelectionHighlight={hideSelectionHighlight}
            alwaysShowActions={isDrawer}
          />
        );
      })
    ) : (
      <Box paddingX="md" paddingBottom="md">
        <Text size="sm" variant="muted">
          No items selected
        </Text>
      </Box>
    );

  return (
    <Box direction="column" ref={ref} border="bottom">
      <Box direction="row" align="center" gap="sm" paddingX="md" paddingY="sm">
        <Text size="xs" weight="medium" variant="muted">
          {label}
        </Text>
        {selections.length > 0 && !hideBadge && (
          <Badge variant="secondary" size="sm">
            {selections.length}
          </Badge>
        )}
      </Box>

      {isDrawer ? (
        <Box paddingX="xs" paddingBottom="xs">
          {items}
        </Box>
      ) : selections.length > 0 ? (
        <ScrollArea style={maxHeight != null ? { maxHeight } : undefined}>
          <Box paddingX="xs" paddingBottom="xs">
            {items}
          </Box>
        </ScrollArea>
      ) : (
        items
      )}
    </Box>
  );
}

EntitySelectorSelected.displayName = "EntitySelectorSelected";

export { EntitySelectorSelected };
